import React, { useState } from 'react'
import { algoliaIndex } from '@/lib/algolia'
import BrowseView from '../../components/browse/BrowseView.jsx'
import PageNotFound from '../../pages/404'
import Head from 'react-helmet'
import { MidFeedAdsProvider } from '../../hooks/useMidfeedAds.jsx'
import { generateSortingLogicMidFeedAds } from '../../domain/useCases/generateSortingLogicMidFeedAds'
import { getParentLocationFromAddressPartsAsSlug } from '../../domain/parentLocations/getParentLocationFromAddressParts'
import { POSTGRESS_ALGOLIA_MAPPINGS } from '../../lib/constants.ts'
import qs from 'qs'
import { createPrimaryFocusMap } from '../../domain/term/useCases/createPrimaryFocusMap.ts'
import { sortAndBoostAdsByPrimaryFocus } from '@/domain/useCases/ads/sortAndBoostAdsByPrimaryFocus'
import { getFilterableTaxonomy } from '@/graphql-fragments/getFilterableTaxonomy.ts'
import { replaceAllDashesWithSpaces } from '../../utils/replaceWith.ts'
import logger from '../../utils/logger/index.ts'
import { getRelatedLocations } from '@/repository/facet/getRelatedLocations'
import { getDepartments } from '@/graphql-fragments/getDepartments'
import { getFacetPageDepartment } from '../../domain/department.js'
import { applyReverseTargeting } from '../../domain/useCases/score/applyReverseTargeting.ts'
const LocationIndex = ({ serverData, data, ...props }) => {
  if (serverData.error) {
    return <PageNotFound />
  }

  const [filterableTaxonomies, setFilterableTax] = useState(
    getFilterableTaxonomy()
  )

  const [primaryFocusMap] = useState(createPrimaryFocusMap())

  const midFeedAds = serverData?.ads?.midFeedAds || []

  const locationName = serverData.locationData?.title
  const parentLocationTitle = serverData?.locationData?.parentLocation

  const { pageType, termData, searchState } = serverData

  const adsForLocation = midFeedAds.filter((ad) => {
    return ad
  })

  const sortedLocations = serverData.sortedLocations

  const sortedAds = adsForLocation
    ? generateSortingLogicMidFeedAds(
        adsForLocation,
        termData.title,
        locationName,
        parentLocationTitle
      )
    : []

  const sortedAndScoredAds = sortAndBoostAdsByPrimaryFocus(
    sortedAds,
    primaryFocusMap[termData.title]
  )

  const department = getFacetPageDepartment({
    locationData: serverData.locationData,
    term: termData
  })
  const departments = getDepartments()

  return (
    <>
      <Head>
        <link
          rel="canonical"
          href={`https://recovery.com/${serverData.locationData.slug}/${termData.slug}/`}
        />
      </Head>
      <MidFeedAdsProvider
        showPickThree={false}
        midFeedAds={sortedAndScoredAds}
      >
        <BrowseView
          {...props}
          path={props.uri}
          termData={termData}
          pageType={pageType}
          data={{
            ...data,
            filterableTaxonomies: filterableTaxonomies,
            location: serverData.locationData,
            term: termData,
            sponsorCenters: { edges: [] },
            hits: serverData.hits,
            hitCount: serverData.hitCount,
            relatedLocationsWithCenterCount:
              serverData.relatedLocationsWithCenterCount
          }}
          pageContext={{
            pageType: pageType,
            searchState: searchState
          }}
          sortedLocations={sortedLocations}
          department={department}
          departments={departments}
        />
      </MidFeedAdsProvider>
    </>
  )
}

export default LocationIndex

export const getServerData = async (props) => {
  const apiBaseUrl = process.env.DEPLOY_URL

  const { url } = props
  const [location, term] = url
    .split('?')[0]
    .replace('/page-data/', '')
    .replace('/page-data.json', '')
    .replaceAll('/', ' ')
    .trim()
    .split(' ')
  try {
    // Only allow a-z, 0-9, hyphens, and underscores and = and ?
    if (!/^[a-z0-9\-_?=&]+$/.test(term)) {
      return {
        status: 404,
        headers: {},
        props: {
          error: {
            message: 'Error getting data'
          }
        }
      }
    }

    const key = process.env.RP_API_KEY
    const apiRoute =
      process.env.FACET_API_ENDPOINT || 'https://admin.rehabpath.com/'
    const CACHE_PREFIX = process.env.CACHE_PREFIX || ''

    const lDataTimer = Date.now()
    const [locationRelatedLocations, termRelatedLocations] = await Promise.all([
      getRelatedLocations(location, 'locations'),
      getRelatedLocations(term, 'terms')
    ]).catch((error) => {
      logger.error({
        error,
        message: `Error in getServerData rendering Facet Page ${location}/${term}`
      })
      return [[], [], []]
    })

    const locationData = await fetch(
      `${apiRoute}api/v2/locations/${location}?key=${key}&term=${term}&cache_prefix=${CACHE_PREFIX}&ad_limit=30`
    )
      .then((res) => res.json())

      .then(({ data }) => {
        return data
      })

    const parentLocationSlug = getParentLocationFromAddressPartsAsSlug(
      locationData.location_address_parts
    )

    const lDataDuration = Date.now() - lDataTimer

    const tDataTimer = Date.now()

    const termData = await fetch(`${apiRoute}api/v2/terms/${term}?key=${key}`)
      .then((res) => res.json())
      .then(({ data }) => {
        return data
      })

    const tDataDuration = Date.now() - tDataTimer

    if (!termData || termData?.error) {
      logger.log({
        message: `Facet Page ${location}/${term} not found`,
        data: props
      })
      return {
        status: 404,
        headers: {},
        props: {
          error: {
            message: 'Error getting data'
          }
        }
      }
    }

    const tAdsTimer = Date.now()

    const adReqParams = qs.stringify({
      key: key,
      location: location,
      parent_location: parentLocationSlug,
      term: term,
      taxonomy: termData.type,
      ad_limit: 20
    })

    const ads = await fetch(`${apiRoute}/api/v2/butler?${adReqParams}`).then(
      (res) => res.json()
    )
    const tAdsDuration = Date.now() - tAdsTimer

    const parentLocation = locationData.address_parts[0]?.title || ''
    const parentLocationWithDashes = replaceAllDashesWithSpaces(parentLocation)

    const searchState = {
      refinementList: {
        [POSTGRESS_ALGOLIA_MAPPINGS[termData.type] || termData.type]: [
          termData.title
        ],
        locations: [locationData.title]
      },
      parentLocation: parentLocation
    }

    const algoliaStart = Date.now()

    const locationAndParentLocationList = [
      locationData.title,
      parentLocationWithDashes
    ].filter(Boolean)

    const locationFilter = locationAndParentLocationList
      .map((location) => `locations:"${location}"`)
      .join(' AND ')
    const termFilter = `${
      POSTGRESS_ALGOLIA_MAPPINGS[termData.type] || termData.type
    }:"${termData.title}"`
    const specializationsFilter = `specializations:"${termData.title}<score=100>"`

    const { hits, nbHits, ...ar } = await algoliaIndex.search('', {
      filters: `${locationFilter} AND ${termFilter} AND NOT status:"Closed" AND NOT status:"Hidden"`,
      page: 0,
      optionalFilters: [specializationsFilter],
      // TODO: This is a temporary fix to get all results. We need to implement pagination
      hitsPerPage: 100
    })

    const algoliaDuration = Date.now() - algoliaStart

    const locationReverseTargeting = await fetch(
      `${apiBaseUrl}/api/rt?type=location&id=${locationData.id}`
    )
      .then((res) => res.json())
      .catch((e) => {
        logger.error({ message: 'Error fetching reverse targeting', data: e })
        return []
      })
    const termReverseTargeting = await fetch(
      `${apiBaseUrl}/api/rt?type=term&id=${termData.termId}`
    )
      .then((res) => res.json())
      .catch((e) => {
        logger.error({ message: 'Error fetching reverse targeting', data: e })
        return []
      })

    const formattedHits = hits.map(({ _highlightResult, ...hit }) => {
      const formattedHit = applyReverseTargeting(
        {
          ...hit,
          slides: hit.slides?.slice(0, 6) || [],
          term_descriptions: hit.term_descriptions?.filter(
            (item) => item.term_title === termData.title
          )
        },
        {
          reverseTargeting: [
            ...termReverseTargeting,
            ...locationReverseTargeting
          ]
        }
      )
      return formattedHit
    })

    const relatedLocations = locationRelatedLocations || termRelatedLocations

    const relatedLocationsWithCenterCountPromises = relatedLocations.map(
      async (relatedLocation) => {
        const response = await fetch(
          `${apiBaseUrl}/api/facet/count?location=${relatedLocation.slug}&term=${term}&taxonomy=${termData.type}`
        )
        if (!response.ok) {
          logger.error({
            message: `HTTP error! status: ${response.status}`,
            data: {
              relatedLocation,
              termData
            }
          })
          return { ...relatedLocation, count: 0 }
        }

        const responseJson = await response.json()

        return { ...relatedLocation, count: responseJson?.data?.count }
      }
    )

    const relatedLocationsWithCenterCount = await Promise.all(
      relatedLocationsWithCenterCountPromises
    )

    const locationsDataWithRelatedLocations = {
      ...locationData,
      sortedLocations: locationRelatedLocations,
      featured_centers: ads.featured_ads || [],
      parentLocation
    }
    const termDataWithRelatedLocations = {
      ...termData,
      sortedLocations: termRelatedLocations
    }

    return {
      status: 200,
      headers: {
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Server-Timing
        'Server-Timing': `lData;dur=${lDataDuration}, butler;dur=${tAdsDuration}, tData;dur=${tDataDuration}, algolia;dur=${algoliaDuration}`,
        // Cache the page for 1 hour (but if a user hits it between 1 and 24 hours, serve the stale version and refresh in the background)
        'Cache-Control':
          'public, s-maxage=3600, stale-while-revalidate=86400, durable'
      },
      props: {
        searchState,
        locationData: locationsDataWithRelatedLocations,
        sortedLocations: locationRelatedLocations || termRelatedLocations,
        termData: termDataWithRelatedLocations,
        ads: ads.data,
        pageType: `Location+${
          POSTGRESS_ALGOLIA_MAPPINGS[termData.type] || termData.type
        }`,
        hits: formattedHits,
        hitCount: nbHits,
        relatedLocationsWithCenterCount: relatedLocationsWithCenterCount
      }
    }
  } catch (error) {
    const message = `Error in getServerData rendering Facet Page ${location}/${term}`
    logger.error({ error, message, data: props })

    return {
      status: 404,
      headers: {},
      props: {
        error: {
          message
        }
      }
    }
  }
}
